import React, { useContext, useState, useRef } from 'react'
import { TextField, Autocomplete, IconButton } from "@mui/material"
import { LoadingContext } from '../M_Utils'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import PasswordChecklist from "react-password-checklist"
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

export function MInputTextField({ label, type, value, onChange, disabled, maxNumbers,
    required, color, secondColor, size, placeholder, placeholderColor, InputProps, onKeyDown, height,
    padding, margin, backgroundColor, options, labelFields, onChangeValue, iconStart,
    iconEnd, autoFocus, onEnter, helperTextMessage, errorMessage, passVerification,
    minLengthverification, valueAgain, noAutoShrink, disabledCheckPasswords, inputHeight, disabledBorderColor,
    minDate, maxDate, onBlur, cy }) {

    const isRequired = required || false
    const ctx = useContext(LoadingContext)
    const [showPassword, setShowPassword] = useState(false)
    const { t } = useTranslation()
    const element = useRef(null)

    function passColor() { return color || ctx.primaryColor || "var(--color-black)" }
    color = passColor()

    function passSecondColor() { return secondColor || ctx.secondaryColor || "var(--color-black)" }
    secondColor = passSecondColor()

    function passBackgroundColor() { return backgroundColor || 'var(--color-white)' }
    backgroundColor = passBackgroundColor()

    function isNumber(e) {
        //* numbers row, numpad, backspace, minus, comma, tab, enter, minus, minus, delete, period, plus
        if ((e.keyCode >= 48 && e.keyCode <= 57) || (e.keyCode >= 96 && e.keyCode <= 105) || e.keyCode === 8 || e.keyCode === 188 || e.keyCode === 9 || e.keyCode === 13 || e.keyCode === 173 || e.keyCode === 189 || e.keyCode === 46 || e.keyCode === 190 || (e.keyCode >= 107 && e.keyCode <= 110) || e.keyCode === 171) {
            return true
        }
        //* ctrl c, ctrl v, ctrlx, arrows
        if ((e.keyCode === 67 && e.ctrlKey) || (e.keyCode === 86 && e.ctrlKey) || (e.keyCode === 88 && e.ctrlKey) || e.keyCode === 37 || e.keyCode === 39) {
            return true
        }
        e.preventDefault()
    }

    const styles = {
        margin: margin,
        width: size || null,
        '& label': {
            color: 'var(--color-black)',
            fontWeight: () => { return disabled && disabledBorderColor ? 'bold' : 'auto' },
            wordBreak: 'break-all',
            fontWeight: 'bold'
        },
        '& label.Mui-focused': {
            color: 'var(--color-black)',
            fontWeight: () => { return disabled && disabledBorderColor ? 'bold' : 'auto' }
        },
        '& .MuiInput-underline:after': { borderBottomColor: () => { return disabled ? disabledBorderColor : color } },
        "& .MuiInputBase-root.Mui-disabled": {
            "& > fieldset": {
                borderColor: () => { return disabled ? disabledBorderColor : 'rgba(0, 0, 0, 0.38) !important' }
            },
            "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: () => { return disabled ? disabledBorderColor : secondColor },
                fontWeight: () => { return disabled && disabledBorderColor ? 'bold' : 'auto' }
            },
        },
        '& .MuiOutlinedInput-input': {
            backgroundColor: backgroundColor,
            padding: padding,
            height: inputHeight || 'auto',
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: () => { return disabled ? disabledBorderColor : color },
                width: size || 'auto',
                height: height,
            },
            '&:hover fieldset': {
                borderColor: () => { return disabled ? disabledBorderColor : color },
            },
            '&.Mui-focused fieldset': {
                borderColor: () => { return disabled ? disabledBorderColor : color },
                boxShadow: `2px 2px 2px ${color}`
            },
            '& input::placeholder': {
                color: placeholderColor || 'rgba(0,0,0,0.4)',
                opacity: 1,
                fontWeight: 500,
            },
            "& legend": {
                // letterSpacing: -1.9,
            }
        },
    }

    function adornments() {
        return {
            startAdornment: (!disabled && iconStart || ''),
            endAdornment: (!disabled && iconEnd || type === 'password' ?
                <IconButton
                    disableRipple
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((prev => !prev))}
                    edge="end"
                    sx={{
                        "&.Mui-focusVisible": {
                            color: "var(--color-black)",
                            backgroundColor: "var(--color-grey-light)"
                        },
                    }}
                >
                    {showPassword ? <VisibilityOff style={{ color: 'var(--color-grey)' }} /> : <Visibility style={{ color: 'var(--color-grey)' }} />}
                </IconButton> : '')
        }
    }

    function inputTypes() {
        if (type === 'number') return 'string'
        if (type === 'password') {
            if (showPassword) {
                return "text"
            } return "password"
        } return type
    }

    return (
        <>{options ?
            <Autocomplete
                noOptionsText={t("NoOptions")}
                options={options}
                sx={styles}
                getOptionLabel={(e) => {
                    let resultString = ''
                    labelFields.forEach(obj => {
                        if (resultString === '') {
                            resultString = resultString.concat(`${e[obj] || ''}`)
                        } else {
                            resultString = resultString.concat(`, ${e[obj] || ''}`)
                        }
                    })
                    return resultString.trim()
                }}
                value={value || null}
                onChange={onChangeValue}
                disabled={disabled}
                renderInput={(params) => <TextField
                    {...params}
                    // slotProps={(e) => (return {e...,InputLabel.})}
                    slotProps={{
                        inputLabel: { shrink: true },
                        input: !disabled ? { ...params.InputProps, ...InputProps } : {}
                    }}
                    data-cy={cy}
                    onChange={onChange}
                    margin="dense"
                    required={isRequired}
                    label={label}
                    variant="outlined"
                    style={{ height: height, textOverflow: 'ellipsis' }}
                    sx={styles}
                    size="small"
                />
                }
            /> :
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <TextField
                    data-cy={cy}
                    error={errorMessage}
                    helperText={helperTextMessage}
                    sx={styles}
                    autoFocus={autoFocus}
                    placeholder={placeholder}
                    slotProps={{
                        inputLabel: !noAutoShrink ? { autoComplete: "none", shrink: true } : { autoComplete: "none" },
                        htmlInput: { maxLength: maxNumbers, min: minDate, max: maxDate },
                        input: adornments(),
                    }}
                    ref={element}
                    required={isRequired}
                    margin="dense"
                    label={label}
                    variant="outlined"
                    fullWidth
                    type={inputTypes()}
                    disabled={disabled}
                    value={value}
                    onChange={onChange}
                    onKeyDown={(e) => {
                        if (onEnter && e.key === 'Enter') {
                            onEnter()
                        }
                        if (e.key === 'Escape' || e.key === 'Enter') {
                            if (iconStart) {
                                element.current.childNodes[1].childNodes[1].blur()
                            } else {
                                element.current.childNodes[1].childNodes[0].blur()
                            }
                            element.current.blur()
                        }
                        if (onKeyDown) {
                            onKeyDown(e)
                        }
                        if (type === "number") {
                            isNumber(e)
                        }
                    }}
                    onBlur={onBlur}
                    style={{ height: height }}
                    size="small"
                />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    {type === 'password' && passVerification ?
                        <PasswordChecklist
                            rules={disabledCheckPasswords ?
                                ["minLength", "specialChar", "number", "capital", "lowercase"] :
                                ["minLength", "specialChar", "number", "capital", "lowercase", "match"]}
                            minLength={minLengthverification}
                            value={value}
                            valueAgain={valueAgain}
                            messages={{
                                minLength: (<span> {t("LabelMaxLength")} {minLengthverification} {t("Labelcaracters")} </span>),
                                specialChar: (<span> {t("LabelSpecialCaracts")} </span>),
                                number: (<span> {t("LabelNeedNumber")} </span>),
                                capital: (<span> {t("LabelUpCarac")} </span>),
                                lowercase: (<span> {t("LabelLowerCase")} </span>),
                                ...(disabledCheckPasswords ? {} : { match: (<span> {t("LabelPassMatch")} </span>) })
                            }}
                        />
                        : <></>
                    }
                </div>
            </div>
        }
        </>
    )
}

MInputTextField.propTypes = {
    label: PropTypes.string,
    type: PropTypes.string,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.number,
    ]),
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    maxNumbers: PropTypes.number,
    minDate: PropTypes.string,
    maxDate: PropTypes.string,
    required: PropTypes.bool,
    color: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
    ]),
    secondColor: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool
    ]),
    size: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
    placeholder: PropTypes.string,
    placeholderColor: PropTypes.string,
    InputProps: PropTypes.object,
    onKeyDown: PropTypes.func,
    height: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
    padding: PropTypes.string,
    margin: PropTypes.string,
    backgroundColor: PropTypes.string,
    options: PropTypes.array,
    labelFields: PropTypes.array,
    onChangeValue: PropTypes.func,
    iconStart: PropTypes.element,
    iconEnd: PropTypes.element,
    autoFocus: PropTypes.bool,
    onBlur: PropTypes.func,
    onEnter: PropTypes.func,
    helperTextMessage: PropTypes.string,
    errorMessage: PropTypes.string,
    passVerification: PropTypes.bool,
    minLengthverification: PropTypes.number,
    valueAgain: PropTypes.string,
    noAutoShrink: PropTypes.bool,
    disabledCheckPasswords: PropTypes.bool,
    inputHeight: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
    disabledBorderColor: PropTypes.string,
    cy: PropTypes.string
}